import React, {
  useEffect,
  useContext,
  useState,
} from 'react';
import { useLazyQuery } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';

import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  AppBar as MuiAppBar,
  Button,
  Dialog,
  DialogTitle,
  FormControl,
  Grid,
  Icon,
  IconButton,
  ListItemIcon,
  MenuItem,
  Select,
  Typography,
  ListSubheader,
  Link,
  Badge,
  Chip,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { ArrowDropDown } from '@material-ui/icons';
import {
  HelpIcon,
  BurgerMenuIcon,
  ProfileIcon,
  SignoutIcon,
  SettingsIcon,
  SearchIcon,
  LeadsIcon,
  CustomersIcon,
  OpportunityIcon,
  ArrowIcon,
  StarIcon,
  SupportIcon,
  DocumentIcon,
  MhubAccountIcon,
} from '../../assets/icons';
import logo from '../../assets/logo/mhub_logo_desktop.svg';
import { AuthContext, PageContext } from '../../contexts';
import { LEAD, CUSTOMER, OPPORTUNITY } from '../../graphql';
import { clientLead, clientCustomer, clientOpportunity } from '../..';
import {
  DropdownMenu,
  IconRoundButton,
} from '..';
// LMS-2843
// import ChatNotification from '../ChatNotification/ChatNotification';
import LangSelector from '../LanguageLocalization';
import GlobalSearch from '../Dropdown/GlobalSearch/GlobalSearch';
import { toCapitalize, toTitleCase } from '../../utils';
import config from '../../config';
import useStyles from './styles';
import {
  APP_ANALYTICS,
  APP_COMMISSION_TRACKER,
  APP_CREDIT_CONTROL,
  APP_CRM,
  APP_LOAN_TRACKER,
  APP_SHOWROOM,
  COUNTRY_MY,
} from '../../localization/features/constants';

const AppBar = (props) => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const { signOut } = authContext.actions;
  const pageContext = useContext(PageContext);
  const { lastReleaseNoteVersionSeen } = pageContext.state;
  const { setWhatsNewModal } = pageContext.actions;

  const history = useHistory();
  const theme = useTheme();
  const classes = useStyles();

  const { toggleMenu } = props;

  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [activeStates, setActiveStates] = useState({
    signOut: false,
  });
  const [expandSearch, setExpandSearch] = useState(false);
  const [searchType, setSearchType] = useState(
    authContext.state.country?.features?.bookingConcept ? 'opportunities' : 'all',
  );
  const [searchResults, setSearchResults] = useState([]);

  const [searchLeadQuery, {
    data: searchLeadData,
    loading: searchLeadLoading,
  }] = useLazyQuery(LEAD.SEARCH(), {
    client: clientLead,
    fetchPolicy: 'no-cache',
  });

  const [searchCustomerQuery, {
    data: searchCustomerData,
    loading: searchCustomerLoading,
  }] = useLazyQuery(CUSTOMER.SEARCH(), {
    client: clientCustomer,
    fetchPolicy: 'no-cache',
  });

  const [searchOpportunityQuery, {
    data: searchOpportunityData,
    loading: searchOpportunityLoading,
  }] = useLazyQuery(OPPORTUNITY.LIST(), {
    client: clientOpportunity,
    fetchPolicy: 'no-cache',
  });

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { apps, user, country } = authContext.state;
  const isBookingConcept = country?.features?.bookingConcept;
  const profileURL = apps.showroom && apps.showroom.webURL ? apps.showroom.webURL : '';
  const accountURL = config.app.mhubAccountURL;
  const newReleaseNoteAvailable = (
    !lastReleaseNoteVersionSeen
    || (lastReleaseNoteVersionSeen && lastReleaseNoteVersionSeen !== config.app.version)
  );

  useEffect(() => {
    async function fetchLeadDetails() {
      const lms = authContext.state.apps.lead_management_v2;
      if (lms) {
        const today = moment();
        const endDate = moment(lms.trialEndsAt);
        const daysLeft = endDate.diff(today, 'days');
        setTrialDaysLeft(daysLeft);
      }
    }
    fetchLeadDetails();
  }, [authContext.state.apps]);

  useEffect(() => {
    if (isDesktop && expandSearch) {
      setExpandSearch(false);
    }
  }, [isDesktop, expandSearch]);

  useEffect(() => {
    const searchLeadResults = searchLeadData
      && searchLeadData.searchLead && searchLeadData.searchLead.body
      ? JSON.parse(searchLeadData.searchLead.body).map((lead) => (
        {
          type: 'lead',
          label: lead.name,
          value: lead.id,
          mobile: lead.msisdn || lead.mobile,
        }
      )) : [];

    const searchCustomerResults = searchCustomerData
      && searchCustomerData.searchCustomers && searchCustomerData.searchCustomers.body
      ? JSON.parse(searchCustomerData.searchCustomers.body).map((customer) => (
        {
          type: 'customer',
          label: customer.contact && customer.contact.name ? customer.contact.name : 'Could not retrieve data',
          isOldContact: (customer.contact && customer.contact.deleted_at),
          value: customer.id,
          mobile: customer.contact ? (customer.contact.msisdn || customer.contact.mobile || 'n/a') : 'Could not retrieve data',
        }
      )) : [];

    const searchBookingResults = searchOpportunityData
      && searchOpportunityData.listOpportunities
      && searchOpportunityData.listOpportunities.opportunities
      ? searchOpportunityData.listOpportunities.opportunities.map((opp) => ({
        type: 'opportunities',
        label: opp.project_name || 'N/A',
        value: opp.id,
        mobile: opp.sr_code || opp.unit_number || '',
      })) : [];

    switch (searchType) {
      case 'lead':
        setSearchResults([...searchLeadResults]);
        break;
      case 'customer':
        setSearchResults([...searchCustomerResults]);
        break;
      case 'opportunities':
        setSearchResults([...searchBookingResults]);
        break;
      default:
        setSearchResults([
          ...searchLeadResults,
          ...searchCustomerResults,
        ]);
        break;
    }
  }, [
    searchType,
    searchLeadData,
    searchCustomerData,
    searchOpportunityData,
  ]);

  const handleOpenState = (name) => () => {
    setActiveStates((s) => ({ ...s, [name]: true }));
  };

  const handleCloseState = (name) => () => {
    setActiveStates((s) => ({ ...s, [name]: false }));
  };

  const handleOpenURL = (url, newTab) => {
    const URL = url;
    if (newTab) {
      window.open(URL, '_blank');
      return;
    }
    window.location.href = URL;
  };

  const handleSignOut = () => {
    signOut();
  };

  const renderUserInformation = () => (
    <Grid container className={classes.userInfo} direction="column">
      <Grid item>
        <Typography variant="subtitle1" className={classes.value}>
          {user.name}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="subtitle2" display="inline">
          {t('appbar.profile.role', 'Role')}
          :&nbsp;
        </Typography>
        <Typography variant="body2" display="inline" className={classes.value}>
          {user.role}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="subtitle2" display="inline">
          {t('appbar.profile.company', 'Company')}
          :&nbsp;
        </Typography>
        <Typography variant="body2" display="inline" className={classes.value}>
          {(user.company && user.company.name) || ''}
        </Typography>
      </Grid>
      <Grid item>
        <Typography variant="subtitle2" display="inline">
          {t('component.label.business_unit', 'Business unit')}
          :&nbsp;
        </Typography>
        <Typography variant="body2" display="inline" className={classes.value}>
          {(user.companyBranch && user.companyBranch.name) || ''}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderMenuHeader = (text) => (
    <Typography variant="subtitle1" className={classes.header}>
      {text}
    </Typography>
  );

  const renderMenuItem = (text, icon, haveBadge) => (
    <>
      {
        icon && (
          <ListItemIcon className={classes.menuIcon}>
            <Badge
              color="error"
              variant="dot"
              invisible={!haveBadge}
              className={classes.badge}
            >
              <Icon><img alt="" src={icon} /></Icon>
            </Badge>
          </ListItemIcon>
        )
      }
      {text}
    </>
  );

  const renderTrialText = () => {
    switch (true) {
      case trialDaysLeft === 1:
        return <div className={classes.trialText}>{t('appbar.trial.last_day', 'last day free trial')}</div>;
      case trialDaysLeft >= 2:
        return (
          <div className={classes.trialText}>
            {t('appbar.trial.days_left', '{{count}} days free trial', { count: trialDaysLeft })}
          </div>
        );
      case trialDaysLeft <= -1:
      default:
        return null;
    }
  };

  const hasAppAccess = (key) => !!(
    apps[key]
    && apps[key].hasAccess
    && Array.isArray(country.enabledApps)
    && country.enabledApps.includes(key)
  );

  const products = apps ? [
    {
      label: t('appbar.product.showroom', 'Showroom'),
      name: APP_SHOWROOM,
      hasAccess: hasAppAccess(APP_SHOWROOM),
      url: (apps.showroom && apps.showroom.webURL)
        || config.app.secureUrl,
    },
    {
      label: t('appbar.product.loan_tracker', 'Loan Tracker'),
      name: APP_LOAN_TRACKER,
      hasAccess: hasAppAccess(APP_LOAN_TRACKER),
      url: (apps.loan_tracker && apps.loan_tracker.webURL)
        || config.app.loanTrackerURL,
    },
    {
      label: t('appbar.product.crm', 'CRM'),
      name: APP_CRM,
      hasAccess: true,
      url: '/',
      tag: (
        <div className={classes.trailTextContainer}>
          {renderTrialText()}
        </div>
      ),
    },
    {
      label: t('appbar.product.credit_control', 'Credit Control'),
      name: APP_CREDIT_CONTROL,
      hasAccess: hasAppAccess(APP_CREDIT_CONTROL),
      url: (apps.credit_control && apps.credit_control.webURL),
    },
    {
      label: t('appbar.product.analytics', 'Analytics'),
      name: APP_ANALYTICS,
      hasAccess: hasAppAccess(APP_ANALYTICS),
      url: (apps.analytics && apps.analytics.webURL)
        || config.app.mhubAnalyticsURL,
    },
    {
      label: t('appbar.product.commission', 'Commission'),
      name: APP_COMMISSION_TRACKER,
      hasAccess: hasAppAccess(APP_COMMISSION_TRACKER),
      url: (apps.commission_tracker && apps.commission_tracker.webURL)
        || config.app.mhubCommissionTrackerURL,
    },
  ] : [];

  const handleOnChangeApp = (e) => {
    const { value } = e.target;
    if (value) {
      window.location.href = value;
    }
  };

  // Apps navigation for mobile view
  const renderAppsDrowdown = () => {
    if (!(apps && !_.isEmpty(apps))) {
      return null;
    }

    return (
      <Grid container justifyContent="center">
        <FormControl className={classes.appsDropdownContainer}>
          <Select
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
            }}
            value="/"
            onChange={handleOnChangeApp}
            className={classes.appsDropdown}
          >
            {
              products.map((product) => (
                product.hasAccess && (
                  <MenuItem key={product.name} value={product.url}>
                    <Typography>{product.label}</Typography>
                  </MenuItem>
                )
              ))
            }
          </Select>
        </FormControl>
      </Grid>
    );
  };

  const currentLocation = useLocation().pathname.substring(1);
  const renderTitle = () => {
    let title = '';
    const secondaryLocationPath = currentLocation.indexOf('/');
    if (secondaryLocationPath >= 0) {
      title = currentLocation.substr(0, secondaryLocationPath);
    } else {
      title = currentLocation;
    }
    switch (title) {
      case '':
        title = t('component.label.home', 'Home');
        break;
      case 'login':
        title = '';
        break;
      case 'access_denied':
        title = t('component.label.access_denied', 'Access denied');
        break;
      case 'not_found':
        title = t('component.label.page_not_found', 'Not found');
        break;
      default:
    }
    return (
      <Typography variant="h6" className={classes.documentTitle}>
        {title.charAt(0).toUpperCase() + title.slice(1)}
      </Typography>
    );
  };

  const renderHeading = () => {
    // to only show apps drowdown when on the homepage
    if (currentLocation === '') {
      return renderAppsDrowdown();
    }
    return renderTitle();
  };

  const minDesktopScreenSizeForAllNavs = useMediaQuery(theme.breakpoints.down('1300'));
  const renderMHubProductNavigations = () => {
    if (!(apps && !_.isEmpty(apps))) {
      return null;
    }

    let productsDesktopNav = [];
    let navsUnderMore = [];
    if (productsDesktopNav && products && products.length > 0) {
      productsDesktopNav = products;
      if (minDesktopScreenSizeForAllNavs && productsDesktopNav.length > 3) {
        navsUnderMore = productsDesktopNav.splice(3, productsDesktopNav.length - 3);
      }
    }

    return (
      <ul className={classes.ul}>
        <li>
          <a href="/" className={classes.logo}>
            <img alt="MHub CRM" src={logo} />
          </a>
        </li>
        {
          productsDesktopNav.map((product) => (
            product.hasAccess && (
              <li key={product.name}>
                <a href={product.url} className={product.name === 'crm' ? 'focus underline' : ''}>
                  {product.label}
                </a>
                {product.tag}
              </li>
            )
          ))
        }
        {
          navsUnderMore.length > 0
          && (
            <li className={classes.moreApps}>
              <DropdownMenu
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                data={navsUnderMore.map((nav) => ({
                  children: <Typography>{nav.label}</Typography>,
                  onClick: () => handleOpenURL(nav.url),
                  hide: !nav.hasAccess,
                }))}
              >
                {({ open, isOpen }) => (
                  <div>
                    <Typography
                      className={`${classes.navigationFont} ${isOpen ? 'focus' : ''}`}
                      component={Link}
                      onClick={open}
                    >
                      {t('component.label.more', 'More')}
                      <ArrowDropDown />
                    </Typography>
                  </div>
                )}
              </DropdownMenu>
            </li>
          )
        }
      </ul>
    );
  };

  const debounceCallBack = _.debounce((callBack) => {
    callBack();
  }, 200);

  const callSearchQueries = (value, type) => {
    if (value) {
      const searchVariables = {
        variables: {
          query: value.toLowerCase(),
          search_all: true,
        },
      };
      const srCodeTokens = value.split(/[-\s]+/).filter(Boolean);
      const bookingSearchVariables = {
        variables: {
          filters: srCodeTokens.map((token) => ({ field: 'sr_code', operator: 'prefix', value: token.toLowerCase() })),
          limit: 10,
          per_query: 10,
          pit_id: '',
          search_after: '',
        },
      };
      switch (type) {
        case 'lead':
          debounceCallBack(() => {
            searchLeadQuery(searchVariables);
          });
          break;
        case 'customer':
          debounceCallBack(() => {
            searchCustomerQuery(searchVariables);
          });
          break;
        case 'opportunities':
          debounceCallBack(() => {
            searchOpportunityQuery(bookingSearchVariables);
          });
          break;
        default:
          debounceCallBack(() => {
            searchLeadQuery(searchVariables);
            searchCustomerQuery(searchVariables);
          });
          break;
      }
    }
  };

  const handleOnSearchTypeChange = (type, searchValue) => {
    setSearchType(type);
    callSearchQueries(searchValue, type);
  };

  const handleOnSearchChange = (value) => {
    callSearchQueries(value, searchType);
  };

  // TODO: Enabled me if zendesk widget chat support is enabled again in the future
  // const handleContactUs = () => {
  //   openSupportWidget();
  // };

  const renderListTitleIcon = (type) => {
    switch (type) {
      case 'lead':
        return LeadsIcon;
      case 'customer':
        return CustomersIcon;
      case 'opportunities':
        return OpportunityIcon;
      default:
        return null;
    }
  };

  const renderListTitle = (type) => {
    switch (type) {
      case 'lead':
        return t('entity.lead', 'lead');
      case 'customer':
        return t('entity.customer', 'customer');
      case 'opportunities':
        return t('component.label.bookings', 'bookings');
      default:
        return null;
    }
  };

  const renderGlobalSearch = () => {
    const searchOptions = isBookingConcept
      ? [
        { icon: OpportunityIcon, label: t('component.label.booking_id', 'Booking ID'), value: 'opportunities' },
        { icon: CustomersIcon, label: toTitleCase(t('component.label.customer_name', 'Customer Name')), value: 'customer' },
      ]
      : [
        { icon: SearchIcon, label: toTitleCase(t('action.all', 'all')), value: 'all' },
        { icon: LeadsIcon, label: toTitleCase(t('entity.lead', 'lead')), value: 'lead' },
        { icon: CustomersIcon, label: toTitleCase(t('entity.customer', 'customer')), value: 'customer' },
      ];

    return (
      <GlobalSearch
        className={`${expandSearch ? classes.mobileGlobalSearch : ''}`}
        loading={searchLeadLoading || searchCustomerLoading || searchOpportunityLoading}
        searchType={searchType}
        searchTypeOptions={searchOptions}
        onSearchTypeChange={handleOnSearchTypeChange}
        inputOptions={searchResults}
        onInputChange={(e, v) => handleOnSearchChange(v)}
        onChange={(e, v) => {
          if (v && v.value) {
            const path = v.type === 'opportunities'
              ? `/opportunities/${v.value}`
              : `/${v.type}s/${v.value}`;
            history.push(path);
          }
        }}
        onClearInput={() => setSearchResults([])}
        groupBy={(option) => option.type}
        filterOptions={(options) => options}
        renderOption={(option) => (
          <Grid container direction="column" className={classes.option}>
            <Grid container className={classes.contactLabel}>
              <Typography variant="subtitle2">
                { toCapitalize(option.label) }
              </Typography>
              { option.isOldContact && <Chip label={t('component.label.old', 'Old')} className={classes.oldChat} /> }
            </Grid>

            <Typography variant="caption">
              { option.mobile }
            </Typography>
          </Grid>
        )}
        renderGroup={(params) => [
          <ListSubheader key={params.key} className={classes.searchListTitle}>
            <img
              alt={params.group}
              src={renderListTitleIcon(params.group)}
              className={classes.icon}
            />
            <Typography variant="caption" align="center">{renderListTitle(params.group)}</Typography>
          </ListSubheader>,
          params.children,
        ]}
      />
    );
  };

  const renderNavigationButton = (title, icon, open) => (
    <IconRoundButton
      imgIconSrc={icon}
      tooltipLabel={title}
      onClick={open}
      showBadge={newReleaseNoteAvailable && title === 'Help'}
    />
  );

  const renderDesktopView = () => (
    <>
      <Grid item>
        {renderMHubProductNavigations()}
      </Grid>
      <Grid item xs={4} className={classes.navigationsContainer}>
        {renderGlobalSearch()}
        {/*
        // LMS-2843
        <ChatNotification />
        */}
        <LangSelector />
        {country.features?.helpButton !== false && (
        <DropdownMenu
          className={classes.menu}
          headerNode={renderMenuHeader(t('component.label.help', 'Help'))}
          data={[
            {
              children: renderMenuItem(t('appbar.help.faq', 'FAQ'), HelpIcon),
              onClick: () => history.push('/faq'),
            },
            {
              children: renderMenuItem(t('appbar.help.new', 'What\'s new'), StarIcon, newReleaseNoteAvailable),
              onClick: () => { setWhatsNewModal(true); },
            },
            {
              children: renderMenuItem(t('navbar.help.support', 'Support'), SupportIcon),
              onClick: () => handleOpenURL(
                'https://support.mhub.my/hc/en-us/categories/360001490114-Lead',
                true,
              ),
            },
            // Zendesk chat widget support has been removed since release/v1.113.0
            // {
            //   children: renderMenuItem('Contact us', SpeechBubbleIcon),
            //   onClick: handleContactUs,
            // },

            // Hide TERMS Of USE for other country temporarily
            // LMS-4404
            country.country_code === COUNTRY_MY
            && {
              children: renderMenuItem(t('component.label.terms', 'Terms of Use'), DocumentIcon),
              onClick: () => handleOpenURL(
                'https://mhub.my/policies/terms_of_use/',
                true,
              ),
            },
          ].filter(Boolean)}
        >
          {({ open }) => renderNavigationButton(t('component.label.help', 'Help'), HelpIcon, open)}
        </DropdownMenu>
        )}
        <DropdownMenu
          className={classes.menu}
          headerNode={renderUserInformation()}
          data={[
            {
              children: renderMenuItem(t('component.label.profile', 'Profile'), ProfileIcon),
              onClick: () => handleOpenURL(`${profileURL}/profile/`),
            },
            {
              children: renderMenuItem(t('appbar.profile.mhub_account', 'MHub account'), MhubAccountIcon),
              onClick: () => handleOpenURL(
                `${accountURL}/`,
                true,
              ),
              hide: !(user.role && user.role === 'Headquarters'),
            },
            {
              children: renderMenuItem(toTitleCase(t('component.text.settings', 'settings')), SettingsIcon),
              onClick: () => history.push('/settings/fields'),
              hide: !(user.role && user.role === 'Headquarters'),
            },
            {
              children: renderMenuItem(t('appbar.profile.sign_out', 'Sign out'), SignoutIcon),
              onClick: handleOpenState('signOut'),
            },
          ]}
        >
          {({ open }) => renderNavigationButton(t('component.label.profile', 'Profile'), ProfileIcon, open)}
        </DropdownMenu>
      </Grid>
    </>
  );

  const renderMobileView = () => (
    <>
      <Grid item>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={toggleMenu}
        >
          <Icon>
            <img alt="menu" src={BurgerMenuIcon} />
          </Icon>
        </IconButton>
      </Grid>
      <Grid item xs={6}>
        {renderHeading()}
      </Grid>
      <Grid item className={classes.mobileNavs}>
        <IconButton
          className={classes.searchButton}
          onClick={() => setExpandSearch(true)}
        >
          <img
            alt="search"
            src={SearchIcon}
            className={`${classes.icon} ${classes.searchListIcon}`}
          />
        </IconButton>
        {/*
          // LMS-2843
          <ChatNotification />
        */}
      </Grid>
    </>
  );

  const renderAppBar = () => {
    if (expandSearch) {
      return (
        <>
          <Grid item>
            <IconButton
              onClick={() => setExpandSearch(false)}
              className={classes.backButton}
            >
              <img alt="menu" src={ArrowIcon} className={classes.backIcon} />
            </IconButton>
          </Grid>
          <Grid item xs={10}>
            {renderGlobalSearch()}
          </Grid>
        </>
      );
    }

    return (
      isDesktop
        ? renderDesktopView()
        : renderMobileView()
    );
  };

  return (
    <MuiAppBar
      className={`${classes.root} ${expandSearch && 'expand'}`}
      color="inherit"
      position="static"
    >
      <Grid
        container
        alignItems="center"
        justifyContent={`${!expandSearch ? 'space-between' : 'flex-start'}`}
      >
        {renderAppBar()}
      </Grid>
      <Dialog open={activeStates.signOut} onClose={handleCloseState('signOut')}>
        <DialogTitle>{`${t('appbar.logut.confirmation', 'Confirm to sign out')}?`}</DialogTitle>
        <Grid container justifyContent="center" className={classes.controls}>
          <Button variant="outlined" onClick={handleCloseState('signOut')}>
            {t('component.label.no', 'No')}
          </Button>
          <Button variant="contained" onClick={handleSignOut}>
            {t('component.label.yes', 'Yes')}
          </Button>
        </Grid>
      </Dialog>
    </MuiAppBar>
  );
};

AppBar.propTypes = {
  toggleMenu: PropTypes.func,
};

AppBar.defaultProps = {
  toggleMenu: () => { },
};

export default AppBar;
